import React, { useState } from 'react';
import { FaUser, FaLock, FaBuilding } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import logo from '../Assets/logo.jpg';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

function RegisterForm() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    organizationName: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.organizationName.trim()) {
      setError(t('register.errors.organizationRequired'));
      return;
    }

    if (!formData.fullName.trim()) {
      setError(t('register.errors.fullNameRequired'));
      return;
    }

    if (!formData.email.trim()) {
      setError(t('register.errors.emailRequired'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('register.errors.passwordMinLength'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return;
    }

    try {
      setLoading(true);
      await register(
        formData.organizationName,
        formData.email,
        formData.password,
        formData.fullName
      );
      // Rediriger vers la page de connexion après inscription réussie
      navigate('/login');
    } catch (err: any) {
      setError(err.message || t('register.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1c2c] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <div className="flex justify-center mb-4">
          <img src={logo} alt="GenIaC Logo" className="h-16" />
        </div>
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">{t('register.title')}</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">{t('register.fields.organizationName.label')}</label>
            <div className="relative">
              <input
                type="text"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleChange}
                placeholder={t('register.fields.organizationName.placeholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none text-gray-900 bg-white"
                disabled={loading}
              />
              <FaBuilding className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">{t('register.fields.fullName.label')}</label>
            <div className="relative">
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder={t('register.fields.fullName.placeholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none text-gray-900 bg-white"
                disabled={loading}
              />
              <FaUser className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">{t('register.fields.email.label')}</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('register.fields.email.placeholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none text-gray-900 bg-white"
                disabled={loading}
              />
              <MdEmail className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">{t('register.fields.password.label')}</label>
            <div className="relative">
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('register.fields.password.placeholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none text-gray-900 bg-white"
                disabled={loading}
              />
              <FaLock className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm text-gray-600 mb-1">{t('register.fields.confirmPassword.label')}</label>
            <div className="relative">
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('register.fields.confirmPassword.placeholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none text-gray-900 bg-white"
                disabled={loading}
              />
              <FaLock className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-300 disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {loading ? t('register.submitting') : t('register.submit')}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-gray-600">
          {t('register.hasAccount')} <Link to="/login" className="text-blue-600 hover:underline">{t('register.loginLink')}</Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterForm;
